import React from 'react';
import styled, { keyframes } from 'styled-components';

import theme from '../../../assets/theme';

type Props = {
  /** Diameter of the mark in px. */
  size?: number;
  /**
   * Fill for the two eye cutouts — set this to the color of the surface the
   * loader sits on so the eyes read as holes. Defaults to the beige app
   * background (where the main full-screen loaders live).
   */
  eyeColor?: string;
  /** Fill for the body. Defaults to the brand orange. */
  bodyColor?: string;
  /** Accessible label (announced to screen readers; no visible text). */
  label?: string;
  className?: string;
};

/**
 * "Hop & blink" loader — the Curipod mark squats, stretches to launch, hangs at
 * the apex, accelerates down, squashes on impact and rebounds, while a soft
 * ground shadow tracks its height and the eyes glance up then blink on landing.
 *
 * Ported 1:1 from the Curipod Loaders design exploration (variant 04). The two
 * eyes are cut out by filling them with the surrounding surface color, so pass
 * `eyeColor` when the background isn't the default beige.
 */
const HopLoader: React.FC<Props> = ({
  size = 120,
  eyeColor = theme.colors.background,
  bodyColor = theme.colors.orange,
  label = 'Loading',
  className,
}) => (
  <Rig role="status" aria-label={label} className={className} $size={size}>
    <Shadow aria-hidden="true" $size={size} />
    <Loader aria-hidden="true" $size={size} $body={bodyColor} $eye={eyeColor}>
      <svg viewBox="0 0 299 298" xmlns="http://www.w3.org/2000/svg">
        <path
          className="body"
          d="M298.184 149.136C298.186 168.62 294.336 187.914 286.854 205.916C279.373 223.917 268.407 240.274 254.582 254.052C240.757 267.829 224.344 278.759 206.281 286.215C188.218 293.671 168.857 297.508 149.306 297.508C128.411 297.508 103.779 296.464 87.0856 282.338C68.0261 266.222 65.3552 240.617 70.5722 217.523C72.9354 205.959 78.0753 195.138 85.5518 185.986C93.0283 176.834 102.619 169.624 113.502 164.972C123.248 161.133 133.647 159.206 144.126 159.3C164.988 159.3 177.88 165.415 188.495 170.451C195.923 173.975 202.236 176.97 209.386 176.97C221.06 176.97 231.241 170.203 231.241 156.307C231.241 133.378 191.655 112.692 146.395 112.692C73.054 112.692 26.3829 159.787 19.1549 221.242C8.10531 201.438 1.75364 179.376 0.586825 156.746C-0.579987 134.117 3.46914 111.522 12.4237 90.6935C21.3782 69.8655 35.0001 51.3579 52.2451 36.59C69.49 21.822 89.8998 11.186 111.909 5.49771C133.918 -0.190565 156.942 -0.78005 179.216 3.77446C201.489 8.32898 222.42 17.9066 240.403 31.7726C258.386 45.6386 272.944 63.4248 282.96 83.7671C292.976 104.109 298.184 126.468 298.184 149.127V149.136Z"
        />
        <circle className="eye" cx="122.602" cy="238.073" r="22.93" />
        <circle className="eye" cx="189.399" cy="238.073" r="22.93" />
      </svg>
    </Loader>
  </Rig>
);

/* Anticipation squat → stretch launch → float at apex → accelerate down →
   squash on impact → tiny rebound → rest. Per-keyframe easings shape the arc. */
const hop = keyframes`
  0%    { transform: translateY(0)     scale(1, 1);
          animation-timing-function: cubic-bezier(.5,.0,.8,.4); }
  12%   { transform: translateY(3%)    scale(1.14, 0.84);
          animation-timing-function: cubic-bezier(.2,.7,.3,1); }
  26%   { transform: translateY(-22%)  scale(0.88, 1.16);
          animation-timing-function: cubic-bezier(.2,.5,.4,1); }
  42%   { transform: translateY(-38%)  scale(0.96, 1.06);
          animation-timing-function: cubic-bezier(.6,0,.8,.5); }
  52%   { transform: translateY(-34%)  scale(0.96, 1.06);
          animation-timing-function: cubic-bezier(.4,0,.8,.6); }
  66%   { transform: translateY(-10%)  scale(0.92, 1.10);
          animation-timing-function: cubic-bezier(.4,0,.9,.4); }
  74%   { transform: translateY(0)     scale(1.22, 0.76);
          animation-timing-function: cubic-bezier(.2,.8,.4,1); }
  82%   { transform: translateY(0)     scale(0.96, 1.04);
          animation-timing-function: cubic-bezier(.4,0,.4,1); }
  90%   { transform: translateY(0)     scale(1.02, 0.99);
          animation-timing-function: cubic-bezier(.4,0,.6,1); }
  100%  { transform: translateY(0)     scale(1, 1); }
`;

/* Eyes: glance up during anticipation, return mid-air, blink shut on impact. */
const hopEyes = keyframes`
  0%    { transform: translate(0, 0)    scaleY(1); }
  10%   { transform: translate(0, -5px) scaleY(1); }
  24%   { transform: translate(0, -3px) scaleY(1); }
  40%   { transform: translate(0, 1px)  scaleY(1); }
  66%   { transform: translate(0, 1px)  scaleY(1); }
  70%   { transform: translate(0, 0)    scaleY(0.12); }
  76%   { transform: translate(0, 0)    scaleY(0.12); }
  82%   { transform: translate(0, 0)    scaleY(1); }
  100%  { transform: translate(0, 0)    scaleY(1); }
`;

/* Shadow: largest & darkest at rest, smallest & lightest at apex. */
const hopShadow = keyframes`
  0%, 100% { transform: translateX(-50%) scale(1, 1);      opacity: 0.42; }
  12%      { transform: translateX(-50%) scale(1.08, 1);   opacity: 0.46; }
  26%      { transform: translateX(-50%) scale(0.78, 0.8); opacity: 0.28; }
  42%      { transform: translateX(-50%) scale(0.45, 0.6); opacity: 0.16; }
  66%      { transform: translateX(-50%) scale(0.78, 0.8); opacity: 0.28; }
  74%      { transform: translateX(-50%) scale(1.18, 1.1); opacity: 0.5; }
  82%      { transform: translateX(-50%) scale(0.98, 0.95); opacity: 0.4; }
  90%      { transform: translateX(-50%) scale(1.02, 1);   opacity: 0.42; }
`;

const Rig = styled.div<{ $size: number }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  /* Extra room above the mark for the hop. */
  height: ${({ $size }) => Math.round($size * 1.25)}px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Loader = styled.div<{ $size: number; $body: string; $eye: string }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  transform-origin: 50% 100%;
  animation: ${hop} 1.5s linear infinite;

  svg {
    width: 100%;
    height: 100%;
    overflow: visible;
  }
  .body {
    fill: ${({ $body }) => $body};
  }
  /* Eyes are filled with the surrounding surface color so they read as cutouts. */
  .eye {
    fill: ${({ $eye }) => $eye};
    transform-box: fill-box;
    transform-origin: center;
    animation: ${hopEyes} 1.5s linear infinite;
  }
`;

const Shadow = styled.div<{ $size: number }>`
  position: absolute;
  bottom: ${({ $size }) => -Math.round($size * 0.077)}px;
  left: 50%;
  width: 70%;
  height: ${({ $size }) => Math.round($size * 0.086)}px;
  border-radius: 50%;
  background: rgba(40, 47, 51, 0.22);
  filter: blur(2px);
  transform-origin: center;
  animation: ${hopShadow} 1.5s linear infinite;
`;

export default HopLoader;
